import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import ChipFilterTsoPq from '@components/Chips/ChipFilterTsoPq';
import ChipTsoPq from '@components/Chips/ChipsTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import SearchFieldTsoPq from '@components/SearchField/SearchFieldTsoPq';
import { StatusChipTsoPq } from '@components/StatusChips/StatusChipTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import UploadFilesTsoPq from '@components/UploadFiles/UploadFiles';
import { RouteMap } from '@constants/routes';
import { faCircleCheck, faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import {
  faChevronRight,
  faCircle,
  faCircleExclamation,
  faHourglass1,
  faInfoCircle,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  customSortIconAsc,
  customSortIconDesc,
  dateTimeSortComparator,
  dateTimeValueFormatter,
  StyledDataGrid,
  StyledEditIconWrapper,
  StyledIcon,
} from '@pages/Masterdata/StyledGridPageComponents';
import { useGetAggregationIntervalGetAllQuery } from '@redux/apiSlices/aggregationIntervalApi';
import { useGetImportOperationGetAllQuery } from '@redux/apiSlices/importOperationApi';
import { useGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppDispatch } from '@redux/hooks';
import { setSelectedImportOperationId } from '@redux/slices/uploadedDataSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { buildMeasurementDeviceDict } from '@utils/deviceHierarchy';
import { useRoleCheck } from '@utils/hooks/useRoleCheck';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  StyledChipContainer,
  StyledHeaderTitle,
  StyledUploadActionsSectionWrapper,
  StyledUploadActionsWrapper,
  StyledUploadFilterSectionWrapper,
  StyledUploadGridWrapper,
  StyledUploadHeaderWrapper,
  StyledUploadPage,
  StyledUploadPageWrapper,
  StyledUploadQuickFiltersWrapper,
  StyledUploadStatusRow,
  StyledUploadStatusRowDivider,
  StyledUploadTypeCustomHeader,
  StyledUploadTypeCustomHeaderText,
} from './StyledUploadDataComponents';

type RowDataUploadedFiles = {
  id: number;
  uploadType: number;
  fileName: string;
  uploadDate: string;
  from?: string | null;
  to?: string | null;
  measurementDeviceId?: number | null;
  aggregationIntervalId?: number | null;
  validationStatus: number;
  importStatus: number;
  plausibilityStatus: number;
  quantileStatus: number;
  comment: string;
};

type StatusType = 'success' | 'error' | 'warning' | 'onHold' | 'inProgress';

interface StatusConfig {
  type: StatusType;
  label: string;
}
const validationStatusMap: Record<number, { type: StatusType; label: string }> = {
  0: { type: 'inProgress', label: 'upload.importStatus.commonStatus.inProgress' },
  1: { type: 'inProgress', label: 'upload.importStatus.commonStatus.inProgress' },
  2: { type: 'success', label: 'upload.importStatus.commonStatus.success' },
  3: { type: 'error', label: 'upload.importStatus.commonStatus.error' },
  4: { type: 'warning', label: 'upload.importStatus.commonStatus.warning' },
};

const importStatusMap: Record<number, { type: StatusType; label: string }> = {
  0: { type: 'onHold', label: 'upload.importStatus.commonStatus.onHold' },
  1: { type: 'inProgress', label: 'upload.importStatus.commonStatus.inProgress' },
  2: { type: 'error', label: 'upload.importStatus.commonStatus.error' },
  3: { type: 'success', label: 'upload.importStatus.commonStatus.success' },
  4: { type: 'warning', label: 'upload.importStatus.commonStatus.warning' },
  5: { type: 'inProgress', label: 'upload.importStatus.commonStatus.inProgress' },
  6: { type: 'inProgress', label: 'upload.importStatus.commonStatus.inProgress' },
};

const plausibilityStatusMap: Record<number, { type: StatusType; label: string }> = {
  0: { type: 'onHold', label: 'upload.importStatus.commonStatus.onHold' },
  1: { type: 'inProgress', label: 'upload.importStatus.commonStatus.inProgress' },
  2: { type: 'error', label: 'upload.importStatus.commonStatus.error' },
  3: { type: 'success', label: 'upload.importStatus.commonStatus.success' },
  4: { type: 'warning', label: 'upload.importStatus.commonStatus.warning' },
  6: { type: 'error', label: 'upload.importStatus.commonStatus.error' },
};

const quantileStatusMap: Record<number, { type: StatusType; label: string }> = {
  0: { type: 'onHold', label: 'upload.importStatus.commonStatus.onHold' },
  1: { type: 'inProgress', label: 'upload.importStatus.commonStatus.inProgress' },
  2: { type: 'success', label: 'upload.importStatus.commonStatus.success' },
  3: { type: 'error', label: 'upload.importStatus.commonStatus.error' },
};

const validationLabelPrefix = 'upload.importStatus.commonLabel.validation';
const importLabelPrefix = 'upload.importStatus.commonLabel.import';
const plausibilityLabelPrefix = 'upload.importStatus.commonLabel.plausibility';
const quantileLabelPrefix = 'upload.importStatus.commonLabel.quantile';

export const getValidityCheckStatus = (row: RowDataUploadedFiles, t: (key: string) => string) => {
  const renderImportStatusChip = (importStatus: number) => {
    switch (importStatus) {
      case 0:
        return <ChipTsoPq statusChips infoBlue textContent={t('upload.importStatus.waitingForImport')} />;
      case 1:
        return <ChipTsoPq statusChips infoBlue textContent={t('upload.importStatus.importing')} />;
      case 2:
        return <ChipTsoPq statusChips errorRed textContent={t('upload.importStatus.error')} />;
      case 3:
        return <ChipTsoPq statusChips successGreen textContent={t('upload.importStatus.imported')} />;
      case 4:
        return <ChipTsoPq statusChips warningOrange textContent={t('upload.importStatus.partiallyImported')} />;
      default:
        return <ChipTsoPq statusChips textContent={t('upload.importStatus.unknown')} />;
    }
  };

  switch (row?.validationStatus) {
    case 0:
    case 1:
      return <ChipTsoPq statusChips infoBlue textContent={t('upload.validationStatus.checkingValidity')} />;
    case 2:
    case 4:
      return renderImportStatusChip(row.importStatus);
    case 3:
      return <ChipTsoPq statusChips errorRed textContent={t('upload.validationStatus.error')} />;
    default:
      return <ChipTsoPq statusChips warningOrange textContent={t('upload.validationStatus.unknown')} />;
  }
};

// @ts-ignore
const StatusChipsRow = ({ row, getChipConfig }) => {
  const valConfig = getChipConfig(row.validationStatus, validationStatusMap, {
    prefixKey: validationLabelPrefix,
  });
  const impConfig = getChipConfig(row.importStatus, importStatusMap, { prefixKey: importLabelPrefix });
  const plConfig = getChipConfig(row.plausibilityStatus, plausibilityStatusMap, {
    prefixKey: plausibilityLabelPrefix,
  });
  const qnConfig = getChipConfig(row.quantileStatus, quantileStatusMap, { prefixKey: quantileLabelPrefix });

  return (
    <StyledUploadStatusRow>
      <StyledChipContainer>
        <StatusChipTsoPq label="V" {...valConfig} tooltipMinWidth="7.5rem" />
        <StyledUploadStatusRowDivider />
      </StyledChipContainer>
      <StyledChipContainer>
        <StatusChipTsoPq label="I" {...impConfig} tooltipMinWidth="7.5rem" />
        <StyledUploadStatusRowDivider />
      </StyledChipContainer>
      <StyledChipContainer>
        <StatusChipTsoPq label="P" {...plConfig} tooltipMinWidth="7.5rem" />
        <StyledUploadStatusRowDivider />
      </StyledChipContainer>
      <StyledChipContainer>
        <StatusChipTsoPq label="Q" {...qnConfig} tooltipMinWidth="7.5rem" />
      </StyledChipContainer>
    </StyledUploadStatusRow>
  );
};

const createColumn = (field: string, headerName: string, options?: Partial<GridColDef>): GridColDef => {
  const baseColumn: GridColDef = {
    field,
    headerName: headerName,
    headerClassName: 'customBackgroundHeader',
    resizable: true,
    ...options,
  };

  if (field === 'fileName') {
    baseColumn.flex = 1;
  }

  return baseColumn;
};

const Upload = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchKey, setSearchKey] = useState<'label' | 'id'>('label');
  const [open, setOpen] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<'success' | 'error' | 'warning' | 'onHold' | 'inProgress' | null>(
    null
  );

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  useEffect(() => {
    setSearchKey('label');
  }, []);

  const { data } = useGetImportOperationGetAllQuery(undefined, { pollingInterval: 15000 });
  const { data: aggregationIntervals } = useGetAggregationIntervalGetAllQuery();

  const aggregationIntervalMap = useMemo(() => {
    const map = new Map<number, string>();
    aggregationIntervals?.externalDtos?.forEach((interval) => {
      if (typeof interval.id === 'number') {
        map.set(interval.id, interval.name);
      }
    });
    return map;
  }, [aggregationIntervals]);

  const { data: tsos } = useGetTsoGetAllQuery();

  const { data: deviceHierarchy } = useGetSubstationGetSubstationDeviceHierarchyQuery(
    // @ts-ignore
    tsos?.externalDtos?.find((t) => t.isLoggedInUsersTso)?.id ?? skipToken
  );

  const measurementDeviceDict = useMemo(() => {
    if (deviceHierarchy?.externalDtos && deviceHierarchy.externalDtos.length > 0)
      return buildMeasurementDeviceDict(deviceHierarchy?.externalDtos ?? []);
    return [];
  }, [deviceHierarchy]);

  const rows = (data?.externalDtos ?? []).map((item) => ({
    id: item.id!,
    uploadType: item.uploadType,
    fileName: item.fileName,
    uploadDate: item.uploadDate,
    from: item.from,
    to: item.to,
    measurementDeviceId: item.measurementDeviceId,
    aggregationIntervalId: item.aggregationIntervalId,
    validationStatus: item.validationStatus,
    importStatus: item.importStatus,
    plausibilityStatus: item.plausibilityStatus,
    quantileStatus: item.quantileCalculationStatus,
    comment: item.comment,
  }));

  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);

  const getUploadType = (uploadType: number) => {
    switch (uploadType) {
      case 1:
        return t('upload.type.auto');
      case 0:
        return t('upload.type.manual');
      default:
        return 'unknown';
    }
  };

  const getChipConfig = (
    status: number | undefined,
    map: Record<number, StatusConfig>,
    options?: { prefixKey?: string }
  ): { type: StatusType; icon: React.ReactNode; tooltip: string } => {
    const config = status !== undefined ? map[status] : undefined;

    if (!config) {
      return { type: 'onHold', icon: <FontAwesomeIcon icon={faCircle} />, tooltip: t('upload.status.unknown') };
    }

    let icon: React.ReactNode;
    switch (config.type) {
      case 'success':
        icon = <FontAwesomeIcon icon={faCircleCheck} />;
        break;
      case 'error':
        icon = <FontAwesomeIcon icon={faCircleXmark} />;
        break;
      case 'warning':
        icon = <FontAwesomeIcon icon={faCircleExclamation} />;
        break;
      case 'inProgress':
        icon = <FontAwesomeIcon icon={faHourglass1} />;
        break;
      default:
        icon = <FontAwesomeIcon icon={faCircle} />;
    }

    const prefixText = options?.prefixKey ? t(options.prefixKey) : '';
    const statusText = t(config.label);
    const tooltip = `${prefixText}${statusText}`;

    return { type: config.type, icon, tooltip };
  };

  const getRowChipTypes = (row: RowDataUploadedFiles) => [
    getChipConfig(row.validationStatus, validationStatusMap).type,
    getChipConfig(row.importStatus, importStatusMap).type,
    getChipConfig(row.plausibilityStatus, plausibilityStatusMap).type,
    getChipConfig(row.quantileStatus, quantileStatusMap).type,
  ];

  const filteredRows = rows.filter((row) => {
    const matchesSearch = Object.values(row).join(' ').toLowerCase().includes(searchQuery.toLowerCase());

    if (!activeFilter) {
      return matchesSearch;
    }

    const rowTypes = getRowChipTypes(row);
    const matchesFilter = rowTypes.includes(activeFilter);

    return matchesSearch && matchesFilter;
  });

  const columnVisibilityModel = useMemo(
    () => ({
      details: supportOrUserAllowed,
    }),
    []
  );

  const columns: GridColDef[] = [
    createColumn('type', t('upload.tableHeader.uploadType'), {
      width: 125,
      description: t('upload.tableHeader.uploadTypeDescription'),
      renderCell: (params) => {
        return getUploadType(params.row.uploadType);
      },
      renderHeader: (params) => {
        return (
          <StyledUploadTypeCustomHeader>
            <StyledUploadTypeCustomHeaderText>
              <TypographyTsoPq typographyType="labelMDBold">{t('upload.tableHeader.uploadType')}</TypographyTsoPq>
            </StyledUploadTypeCustomHeaderText>
            <IconButtonTsoPq
              buttonType="transparentNeutralNoEffect"
              sizeVariant="s"
              multilineTooltip
              tooltipMinWidth="5.5rem"
              icon={faInfoCircle}
              tooltip={t('upload.tableHeader.uploadTypeDescription')}
            />
          </StyledUploadTypeCustomHeader>
        );
      },
    }),
    createColumn('fileName', t('upload.tableHeader.fileName'), {
      minWidth: 275,
    }),
    createColumn('uploadDate', t('upload.tableHeader.uploadedOn'), {
      width: 150,
      valueGetter: (_, row) => row.uploadDate,
      valueFormatter: dateTimeValueFormatter,
      sortComparator: dateTimeSortComparator,
    }),
    createColumn('from', t('upload.tableHeader.startPeriod'), {
      width: 150,
      valueGetter: (_, row) => row.from,
      valueFormatter: dateTimeValueFormatter,
      sortComparator: dateTimeSortComparator,
    }),
    createColumn('to', t('upload.tableHeader.endPeriod'), {
      width: 150,
      valueGetter: (_, row) => row.to,
      valueFormatter: dateTimeValueFormatter,
      sortComparator: dateTimeSortComparator,
    }),
    createColumn('substation', t('upload.tableHeader.substation'), {
      width: 200,
      valueGetter: (_, row) =>
        row.measurementDeviceId ? `${measurementDeviceDict[row.measurementDeviceId]?.level1 ?? ''}` : '',
    }),
    createColumn('measurementDeviceId', t('upload.tableHeader.measuringDevice'), {
      minWidth: 275,
      valueGetter: (value) =>
        value ? `${measurementDeviceDict[value]?.level2 ?? ''} - ${measurementDeviceDict[value]?.level3 ?? ''}` : '',
    }),
    createColumn('aggregationIntervalId', t('upload.tableHeader.aggregationInterval'), {
      width: 175,
      valueGetter: (value) => (value ? t(`upload.${aggregationIntervalMap.get(value)}`) : ''),
    }),
    createColumn('status', t('upload.tableHeader.status'), {
      width: 200,
      sortable: false,
      renderCell: (params) => <StatusChipsRow row={params.row} getChipConfig={getChipConfig} />,
    }),
    createColumn('details', '', {
      width: 52,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <PermissionGate roles={userOrSupportRestriction}>
            <StyledEditIconWrapper>
              <IconButtonTsoPq
                sizeVariant="s"
                buttonType="transparentPrimary"
                icon={faChevronRight}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setSelectedImportOperationId(params.row.id));
                  navigate(`${RouteMap.UPLOAD_ROUTE}/${params.row.id}`);
                }}
              />
            </StyledEditIconWrapper>
          </PermissionGate>
        );
      },
    }),
  ];

  return (
    <StyledUploadPage>
      <StyledUploadPageWrapper>
        <StyledUploadHeaderWrapper>
          <StyledHeaderTitle>{t('upload.pageTitle')}</StyledHeaderTitle>
          <StyledUploadActionsSectionWrapper>
            <StyledUploadFilterSectionWrapper>
              <TypographyTsoPq
                typographyType="labelMDBold"
                color={tsoPqTheme.palette.customTextColor.color_font_ft_helping_text}
              >
                {t('upload.chipFilter.quickFilters')}
              </TypographyTsoPq>
              <StyledUploadQuickFiltersWrapper>
                <ChipFilterTsoPq
                  textContent={t('upload.chipFilter.success')}
                  active={activeFilter === 'success'}
                  onClick={() => setActiveFilter((prev) => (prev === 'success' ? null : 'success'))}
                />

                <ChipFilterTsoPq
                  textContent={t('upload.chipFilter.error')}
                  active={activeFilter === 'error'}
                  onClick={() => setActiveFilter((prev) => (prev === 'error' ? null : 'error'))}
                />

                <ChipFilterTsoPq
                  textContent={t('upload.chipFilter.warning')}
                  active={activeFilter === 'warning'}
                  onClick={() => setActiveFilter((prev) => (prev === 'warning' ? null : 'warning'))}
                />
              </StyledUploadQuickFiltersWrapper>
            </StyledUploadFilterSectionWrapper>
            <StyledUploadActionsWrapper>
              <SearchFieldTsoPq
                searchQuery={searchQuery}
                searchKey={searchKey}
                handleChange={setSearchQuery}
                resetSearch={() => setSearchQuery('')}
                customwidth="35.4375rem"
                customLabel={t('upload.searchPlaceholder')}
              />
              <UploadFilesTsoPq openModal={open} handleModalState={setOpen} />
              <PermissionGate roles={userOrSupportRestriction}>
                <ButtonTsoPq
                  buttonType={'filledPrimary'}
                  textContent={t('upload.fileUpload.title')}
                  iconLeft={<StyledIcon icon={faPlus} />}
                  onClick={() => setOpen(true)}
                />
              </PermissionGate>
            </StyledUploadActionsWrapper>
          </StyledUploadActionsSectionWrapper>
        </StyledUploadHeaderWrapper>
        <StyledUploadGridWrapper>
          <StyledDataGrid
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row')}
            rows={filteredRows.length > 0 ? filteredRows : []}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnReorder
            disableColumnPinning
            hideFooterSelectedRowCount
            isCellEditable={() => false}
            hideFooter
            getRowHeight={() => 47}
            getRowId={(row) => row.id}
            showCellVerticalBorder
            slots={{ columnSortedAscendingIcon: customSortIconAsc, columnSortedDescendingIcon: customSortIconDesc }}
            disableColumnMenu
            localeText={{
              noRowsLabel: t('general.grid.noRows'),
              noResultsOverlayLabel: t('general.grid.noMatchingRows'),
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'uploadDate', sort: 'desc' }],
              },
            }}
            columnVisibilityModel={columnVisibilityModel}
          />
        </StyledUploadGridWrapper>
      </StyledUploadPageWrapper>
    </StyledUploadPage>
  );
};

export default Upload;
